// The sender screener, pure parts: which list a sender belongs to, how a decision changes it
// locally, the words on each row, and which Hidden rows one screen-out decision set aside (so its
// Undo can put them back). Nothing here changes Gmail; the server decides once per sender.
import type { HiddenThreadView, SenderSuggestion, SenderView, ThreadView } from "@shared/api";
import {
  DESTINATION_LABEL,
  DESTINATIONS,
  deliveryLabel,
  type Delivery,
  type Destination,
} from "@shared/routing";
import type { SenderListParams, SenderScreen } from "@web/lib/sendersApi";
import { formatAgo } from "@web/lib/time";

export type ScreenerView = "awaiting" | "allowed" | "screened_out" | "lists";

export const SCREENER_VIEWS: readonly ScreenerView[] = [
  "awaiting",
  "allowed",
  "screened_out",
  "lists",
];

export const SCREENER_VIEW_LABEL: Record<ScreenerView, string> = {
  awaiting: "Waiting for you",
  allowed: "Allowed",
  screened_out: "Screened out",
  lists: "Lists",
};

export interface ScreenerSearch {
  view?: Exclude<ScreenerView, "awaiting"> | undefined;
}

export function parseScreenerSearch(raw: Record<string, unknown>): ScreenerSearch {
  const view = raw.view;
  return view === "allowed" || view === "screened_out" || view === "lists" ? { view } : {};
}

export const SCREENER_LIMIT = 200;

/** Lists (newsletters and other list mail) are picked out of every sender by `isList`. */
export function listParamsFor(view: ScreenerView): SenderListParams {
  return view === "awaiting"
    ? { awaiting: true, limit: SCREENER_LIMIT }
    : view === "lists"
      ? { limit: SCREENER_LIMIT }
      : { screen: view, limit: SCREENER_LIMIT };
}

/** The senders a view shows from what its list query returned. */
export function sendersInView(list: readonly SenderView[], view: ScreenerView): SenderView[] {
  return view === "lists" ? list.filter((s) => s.isList) : [...list];
}

export function senderKey(sender: Pick<SenderView, "accountId" | "address">): string {
  return `${sender.accountId}:${sender.address.toLowerCase()}`;
}

/** The server's rule for "first time, not decided yet" (server/mail/senders.ts toSenderView). */
export function isAwaiting(
  sender: Pick<SenderView, "screen" | "outboundN" | "inboundN" | "isList">,
): boolean {
  return (
    sender.screen === "none" && sender.outboundN === 0 && sender.inboundN > 0 && !sender.isList
  );
}

/** Whether a sender belongs in a cached list fetched with `params`. */
export function matchesParams(sender: SenderView, params: SenderListParams): boolean {
  if (params.accountId && sender.accountId !== params.accountId) return false;
  if (params.screen && sender.screen !== params.screen) return false;
  if (params.awaiting && !sender.awaitingDecision) return false;
  return true;
}

/** A sender as it looks right after a decision (before the server answers). */
export function withScreen(sender: SenderView, screen: SenderScreen, at: string): SenderView {
  const next = { ...sender, screen, screenedAt: screen === "none" ? null : at };
  return { ...next, awaitingDecision: isAwaiting(next) };
}

/** Apply a changed sender to one cached list: replace it, drop it if it no longer belongs. */
export function patchSenderList(
  list: readonly SenderView[],
  params: SenderListParams,
  changed: SenderView,
): SenderView[] {
  const key = senderKey(changed);
  return list
    .map((s) => (senderKey(s) === key ? changed : s))
    .filter((s) => senderKey(s) !== key || matchesParams(s, params));
}

export function senderName(sender: Pick<SenderView, "name" | "address">): string {
  const name = sender.name?.trim().replace(/^["']|["']$/g, "");
  if (name) return name;
  const local = sender.address.split("@")[0];
  return local || sender.address;
}

export function messagesLabel(inboundN: number): string {
  if (inboundN <= 0) return "No mail from them yet";
  return inboundN === 1 ? "1 message" : `${inboundN} messages`;
}

export function lastSeenLabel(sender: Pick<SenderView, "lastInAt">, now: number): string | null {
  return sender.lastInAt ? `last wrote ${formatAgo(sender.lastInAt, now)}` : null;
}

export function screenStateLabel(
  sender: Pick<SenderView, "screen" | "awaitingDecision" | "outboundN">,
): string {
  if (sender.screen === "allowed") return "Allowed";
  if (sender.screen === "screened_out") return "Screened out · new mail goes to Set aside";
  if (sender.awaitingDecision) return "Writing for the first time · waiting for you";
  if (sender.outboundN > 0) return "Someone you’ve written to";
  return "Not screened";
}

/** The toast after a decision. */
export function decisionMessage(
  sender: Pick<SenderView, "name" | "address">,
  screen: SenderScreen,
) {
  const who = senderName(sender);
  switch (screen) {
    case "allowed":
      return `Allowed ${who}. Their mail reaches your feed as usual.`;
    case "screened_out":
      return `Screened out ${who}. Their mail goes to Set aside.`;
    case "none":
      return `${who} is back to undecided.`;
  }
}

/**
 * Hidden rows that one screen-out decision set aside: suppressed with the screener's reason, not
 * restored, recorded at or after the decision, in the sender's account, from that sender.
 */
export function setAsideByDecision(
  hidden: readonly HiddenThreadView[],
  sender: Pick<SenderView, "accountId" | "address">,
  decidedAt: string,
  slackMs = 5000,
): HiddenThreadView[] {
  const since = new Date(decidedAt).getTime() - slackMs;
  const address = sender.address.toLowerCase();
  return hidden.filter(
    (h) =>
      h.disposition === "suppressed" &&
      !h.restoredAt &&
      /screened out/i.test(h.reason) &&
      new Date(h.at).getTime() >= since &&
      h.threadKey.startsWith(`${sender.accountId}:`) &&
      h.thread?.latestFrom?.email.toLowerCase() === address,
  );
}

export interface ScreenTarget {
  accountId: string;
  address: string;
  name: string | null;
}

/** Who a thread is from, for the "Sender:" line: the latest sender who is not the owner. */
export function screenTarget(thread: ThreadView | null | undefined): ScreenTarget | null {
  if (!thread) return null;
  const owner = thread.thread.accountEmail.toLowerCase();
  const fromOthers = [...thread.messages]
    .reverse()
    .map((m) => m.from)
    .find((from) => !!from?.email && from.email.toLowerCase() !== owner);
  const latest = thread.thread.latestFrom;
  const from =
    fromOthers ?? (latest?.email && latest.email.toLowerCase() !== owner ? latest : null);
  if (!from?.email.includes("@")) return null;
  return {
    accountId: thread.thread.accountId,
    address: from.email.toLowerCase(),
    name: from.name?.trim() || null,
  };
}

// ---------------------------------------------------------------------------------------------
// U8 destinations: the screener's yes becomes a choice (1 Cards · 2 Brief only · 3 Receipts ·
// 4 Screen out). All routing is local; Gmail is untouched.
// ---------------------------------------------------------------------------------------------

/** Keys 1–4, in this order. */
export const DESTINATION_KEYS: readonly Destination[] = DESTINATIONS;

/** Where the sender's mail goes now, or null while undecided (or allowed with no choice). */
export function currentDestination(
  sender: Pick<SenderView, "screen" | "destination">,
): Destination | null {
  if (sender.screen === "screened_out") return "screen_out";
  return sender.screen === "allowed" ? (sender.destination ?? null) : null;
}

/** A sender as it looks right after a destination choice (before the server answers). */
export function withDestination(
  sender: SenderView,
  destination: Destination,
  delivery: Delivery | null | undefined,
  at: string,
): SenderView {
  const screen = destination === "screen_out" ? "screened_out" : "allowed";
  return {
    ...withScreen(sender, screen, at),
    destination,
    ...(delivery !== undefined ? { delivery } : {}),
  };
}

/** The toast after a destination choice. */
export function destinationMessage(
  sender: Pick<SenderView, "name" | "address">,
  destination: Destination,
  delivery: Delivery | null | undefined,
  defaultLabel: string,
): string {
  const who = senderName(sender);
  switch (destination) {
    case "cards":
      return `${who}: Cards. Their mail can become a card when it needs you.`;
    case "brief": {
      const when = delivery ? deliveryLabel(delivery) : defaultLabel;
      return `${who}: Brief only. Their mail shows up in ${when}, never as a card.`;
    }
    case "receipts":
      return `${who}: Receipts. Kept and searchable, never a card or a brief item.`;
    case "screen_out":
      return `Screened out ${who}. Their mail goes to Set aside.`;
  }
}

/** "Claude suggests Brief only: “…their words…”". */
export function suggestionLine(s: SenderSuggestion): string {
  return `Claude suggests ${DESTINATION_LABEL[s.destination]}: “${s.quote}”`;
}
